/**
 * Verify Email Page
 *
 * Displays email verification waiting screen with resend option.
 * Polls for verification status and redirects to onboarding when verified.
 *
 * @module pages/auth/VerifyEmailPage
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTitle } from '@/hooks';
import { isUsingEmulators, functions } from '@/lib/firebase';
import { Button, Alert } from '@/components/ui';
import { Mail, RefreshCw, ExternalLink, Terminal } from 'lucide-react';

// =============================================================================
// Component
// =============================================================================

export function VerifyEmailPage() {
  usePageTitle('Verify Email');

  const navigate = useNavigate();
  const { firebaseUser, signOut } = useAuth();

  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [verificationLink, setVerificationLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Poll for email verification status
  // ---------------------------------------------------------------------------

  const checkVerification = useCallback(async () => {
    if (!firebaseUser) return;

    try {
      await firebaseUser.reload();
      if (firebaseUser.emailVerified) {
        navigate('/onboarding', { replace: true, state: { emailVerified: true } });
      }
    } catch (err) {
      console.error('Error checking verification status:', err);
    }
  }, [firebaseUser, navigate]);

  useEffect(() => {
    // Check immediately on mount
    checkVerification();

    // Then poll every 3 seconds
    const interval = setInterval(checkVerification, 3000);

    return () => clearInterval(interval);
  }, [checkVerification]);

  // Redirect if already verified
  useEffect(() => {
    if (firebaseUser?.emailVerified) {
      navigate('/onboarding', { replace: true });
    }
  }, [firebaseUser?.emailVerified, navigate]);

  // ---------------------------------------------------------------------------
  // Fetch verification link from emulator (dev only)
  // ---------------------------------------------------------------------------

  const fetchVerificationLink = useCallback(async () => {
    if (!isUsingEmulators || !firebaseUser?.email) return;

    try {
      const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID;
      const response = await fetch(
        `http://localhost:28099/emulator/v1/projects/${projectId}/oobCodes`
      );

      if (!response.ok) return;

      const data = await response.json();
      // Find the most recent verification code for this email
      const codes = data.oobCodes || [];
      const verificationCode = codes
        .filter(
          (code: { email: string; requestType: string }) =>
            code.email === firebaseUser.email &&
            code.requestType === 'VERIFY_EMAIL'
        )
        .pop();

      if (verificationCode?.oobLink) {
        setVerificationLink(verificationCode.oobLink);
      }
    } catch (err) {
      console.error('Failed to fetch OOB codes:', err);
    }
  }, [firebaseUser?.email]);

  // Fetch verification link on mount and after resending
  useEffect(() => {
    if (isUsingEmulators) {
      fetchVerificationLink();
    }
  }, [fetchVerificationLink, resendSuccess]);

  // ---------------------------------------------------------------------------
  // Handle resend verification email
  // ---------------------------------------------------------------------------

  const handleResend = async () => {
    if (!firebaseUser) return;

    setResending(true);
    setError(null);
    setResendSuccess(false);

    try {
      const sendVerificationEmail = httpsCallable<void, { success: boolean }>(
        functions,
        'sendVerificationEmail'
      );
      await sendVerificationEmail();
      setResendSuccess(true);
    } catch (err: unknown) {
      const error = err as { code?: string; message?: string };
      if (error.code === 'functions/resource-exhausted' || error.message?.includes('too-many-requests')) {
        setError('Too many requests. Please wait before trying again.');
      } else {
        setError('Failed to send verification email. Please try again.');
      }
    } finally {
      setResending(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Handle sign out
  // ---------------------------------------------------------------------------

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (!firebaseUser) {
    return null;
  }

  return (
    <div className="min-h-screen flex overflow-auto no-scrollbar">
      {/* Left side - Content */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-brand-500 overflow-hidden">
              <svg className="w-10 h-10" viewBox="0 0 32 32" fill="none">
                <path
                  d="M7 10L13 16L7 22"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.5"
                />
                <path
                  d="M13 10L19 16L13 22"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  opacity="0.75"
                />
                <path
                  d="M19 10L25 16L19 22"
                  stroke="white"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className="font-display font-semibold text-xl text-gray-900">
              Hummingbird
            </span>
          </div>

          {/* Email Icon */}
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-brand-50 rounded-full">
              <Mail className="h-12 w-12 text-brand-600" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-display font-semibold text-gray-900">
              Check your email
            </h1>
            <p className="text-gray-500 mt-2">
              We sent a verification link to
            </p>
            <p className="font-medium text-gray-900 mt-1">
              {firebaseUser.email}
            </p>
          </div>

          {/* Status Messages */}
          {error && (
            <Alert variant="error" className="mb-6" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          {resendSuccess && (
            <Alert variant="success" className="mb-6">
              Verification email sent!
            </Alert>
          )}

          {/* Instructions */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600">
              Click the link in your email to verify your account. If you don't
              see it, check your spam folder.
            </p>
          </div>

          {/* Waiting indicator */}
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-6">
            <RefreshCw className="h-4 w-4 animate-spin" />
            <span>Waiting for verification...</span>
          </div>

          {/* Development Helper - Only shows when using emulators */}
          {isUsingEmulators && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Terminal className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm flex-1">
                  <p className="font-medium text-amber-900">Development Mode</p>
                  <p className="text-amber-700 mt-1">
                    Using Firebase Emulators. Skip email verification for faster testing.
                  </p>

                  {/* Verification Link (if available) */}
                  {verificationLink && (
                    <div className="mt-3 p-2 bg-amber-100 rounded border border-amber-300">
                      <p className="text-xs font-medium text-amber-800 mb-1">Verification Link:</p>
                      <a
                        href={verificationLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-amber-900 underline break-all hover:text-amber-700"
                      >
                        {verificationLink}
                      </a>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    <a
                      href="http://localhost:28000/auth"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-amber-800 font-medium hover:text-amber-900"
                    >
                      Auth Emulator
                      <ExternalLink className="h-3 w-3" />
                    </a>
                    <a
                      href="http://localhost:8025"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-amber-800 font-medium hover:text-amber-900"
                    >
                      Mailpit
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <Button
              variant="secondary"
              fullWidth
              onClick={handleResend}
              loading={resending}
              disabled={resendSuccess}
            >
              Resend verification email
            </Button>

            <Button variant="ghost" fullWidth onClick={handleSignOut}>
              Sign out and try again
            </Button>
          </div>

          {/* Help text */}
          <p className="text-center text-xs text-gray-500 mt-6">
            Having trouble?{' '}
            <a
              href="mailto:support@hummingbirdapi.com"
              className="text-brand-600 hover:text-brand-700"
            >
              Contact support
            </a>
          </p>
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-brand-500 to-brand-600 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern
                id="grid"
                width="40"
                height="40"
                patternUnits="userSpaceOnUse"
              >
                <path
                  d="M 40 0 L 0 0 0 40"
                  fill="none"
                  stroke="white"
                  strokeWidth="1"
                />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative flex flex-col justify-center px-12 text-white">
          <h2 className="text-4xl font-display font-semibold mb-4">
            Almost there!
          </h2>
          <p className="text-xl text-white/80 max-w-md">
            Verify your email to unlock your free 2,000 API requests and start
            building.
          </p>
        </div>
      </div>
    </div>
  );
}
